import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Loader from './LoginLoader';
import {
  getLocales,
  getTimezones,
  logoutRequest,
  registration,
} from '../../../actions/AuthActions';
import FormHelperText from '../Form/FormHelperText';

const useStyles = makeStyles(() => ({
  registrationInfo: {
    margin: '0 0 20px 0',
    width: 430,
    '@media (max-width: 575px)': {
      width: '100%',
    },
  },
  registrationForm: {
    margin: '70px 0 100px 0',
    width: 450,
    '@media (max-width: 575px)': {
      width: '100%',
    },
  },
  formControl: {
    margin: '0 0 10px 0',
    '&&': {
      border: 'none',
      width: '100%',
    },

    '& input': {
      width: '100%',
      padding: '2px 10px',
      height: 40,
      border: 'none',
      outline: 'none',
      fontSize: '1rem',
    },

    '& label': {
      transform: 'none !important',
      position: 'relative !important',
      height: '20px !important',
      alignSelf: 'flex-start !important',
      fontSize: '1rem !important',
      marginBottom: '0.5rem !important',
      fontWeight: 'normal !important',
      fontStyle: 'normal !important',
      fontStretch: 'normal !important',
      lineHeight: '1.62 !important',
      letterSpacing: 'normal !important',
      fontFamily: 'sans-serif !important',
    },
  },
  formInput: {
    '& div': {
      background: 'white',
      borderRadius: 4,
      border: '1px solid #b5b5b5',
    },
    '& div:before': {
      content: '',
      border: 'none !important',
      transition: 'none',
    },
    '& div:after': {
      content: '',
      transition: 'none',
      border: 'none !important',
    },
  },
  formSelect: {
    '& div > div': {
      background: 'white',
      borderRadius: 4,
      border: '1px solid #b5b5b5',
    },
    '& div > div:hover': {
      border: '1px solid #b5b5b5',
    },
    '& div > div:focus': {
      borderColor: '#b5b5b5',
    },
  },
  error: {
    '& div': {
      border: '1px solid #F44336 !important',
    },
    '& label': {
      color: '#F44336 !important',
    },
  },
}));

const RegistrationForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { pending, registrationError, locales = [], timezones = [] } = useSelector(
    (state) => state.auth
  );
  const hasError = !!registrationError;
  const { message = '', status = '' } = registrationError ?? {};
  const registrationErrorMessage = `Error: ${message}${status ? ` Code: ${status}` : ''}`;

  const actorCodeWithDomain = localStorage.getItem('actorCodeWithDomain') || '';
  const domain = actorCodeWithDomain.substring(actorCodeWithDomain.lastIndexOf('@') + 1);
  const userNameWithoutDomain = actorCodeWithDomain.substring(
    0,
    actorCodeWithDomain.lastIndexOf('@')
  );

  const [logonUser, setLogonUser] = useState(userNameWithoutDomain);
  const [username, setUsername] = useState('');
  const [language, setLanguage] = useState('');
  const [timezone, setTimezone] = useState('');
  const [email, setEmail] = useState('');
  const [isValidForm, setIsValidForm] = useState(false);

  useEffect(() => {
    dispatch(getLocales());
    dispatch(getTimezones());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsValidForm(logonUser && username && language && timezone && email);
  }, [logonUser, username, language, timezone, email]);

  const handleLogonUserChange = ({ target: { value } }) => setLogonUser(value);
  const handleUsernameChange = ({ target: { value } }) => setUsername(value);
  const handleLanguageChange = ({ target: { value } }) => setLanguage(value);
  const handleTimezoneChange = ({ target: { value } }) => setTimezone(value);
  const handleEmailChange = ({ target: { value } }) => setEmail(value);

  const handleCancelRegistration = () => {
    dispatch(logoutRequest());
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    dispatch(
      registration({
        LogonUser: logonUser,
        UserName: username,
        EmailAddress: email,
        Domain: domain,
        Locale: language,
        TimeZone: timezone,
      })
    );
  };

  const renderItems = (items) =>
    Object.keys(items).map((item, idx) => (
      <MenuItem key={`${idx}-${items[item]}`} value={items[item]}>
        {item}
      </MenuItem>
    ));

  const registrationInfo =
    'Your account information and preference cannot be found. Please complete the fields below to register your account. You will be notified once your request is approved.';

  return (
    <div className={classes.registrationForm}>
      <div className={classes.registrationInfo}>{registrationInfo}</div>
      <div>
        <form onSubmit={handleSubmit}>
          <div id="logon-user" ui-ta="logon-user">
            <TextField
              className={[classes.formControl, classes.formInput, hasError ? classes.error : '']}
              error={hasError}
              label="Logon User"
              value={logonUser}
              onChange={handleLogonUserChange}
              inputProps={{ autoComplete: 'off', autoFocus: true }}
              disabled
            />
          </div>
          <div id="username" ui-ta="username">
            <TextField
              className={[classes.formControl, classes.formInput, hasError ? classes.error : '']}
              error={hasError}
              label="Name"
              value={username}
              onChange={handleUsernameChange}
              inputProps={{ autoComplete: 'off' }}
            />
          </div>
          <div id="language" ui-ta="language">
            <Select
              className={[classes.formControl, classes.formSelect, hasError ? classes.error : '']}
              error={hasError}
              label="Language"
              value={language}
              onChange={handleLanguageChange}
              canDeselect={false}
            >
              {renderItems(locales)}
            </Select>
          </div>
          <div id="timezone" ui-ta="timezone">
            <Select
              className={[classes.formControl, classes.formSelect, hasError ? classes.error : '']}
              error={hasError}
              label="Time Zone"
              value={timezone}
              onChange={handleTimezoneChange}
              canDeselect={false}
            >
              {renderItems(timezones)}
            </Select>
          </div>
          <div id="email" ui-ta="email">
            <TextField
              className={[classes.formControl, classes.formInput, hasError ? classes.error : '']}
              error={hasError}
              label="E-mail Address"
              value={email}
              onChange={handleEmailChange}
              inputProps={{ autoComplete: 'off' }}
            />
          </div>
          {hasError && (
            <FormHelperText id="statusMessage" error>
              {registrationErrorMessage}
            </FormHelperText>
          )}

          <Button
            type="submit"
            color="primary"
            id="registration-btn"
            ui-ta="registration-btn"
            variant="primary"
            style={{ marginTop: 20 }}
            disabled={pending || !isValidForm}
          >
            {pending ? <Loader /> : 'Register'}
          </Button>
          <Button
            color="primary"
            id="cancel-registration-btn"
            ui-ta="cancel-registration-btn"
            variant="secondary"
            style={{ marginTop: 20 }}
            onClick={handleCancelRegistration}
            disabled={pending}
          >
            {'Back to login'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default RegistrationForm;
